import { useContext, useState } from 'react';
import styled from 'styled-components';

import { MapContext } from '../context';
import { POLICIES } from '../data/policies';
import { formatter, showSumOfPolicies } from '../helpers';
import { ActiveStateOrUSId, State, StateId } from '../types';
import Button from './Button';
import Select from './Select';

const StyledCountyTable = styled.div`
	background-color: #fff;
	border: 1px solid #999;
	border-radius: 4px;
	display: grid;
	grid-gap: 0.5rem;
	height: 100%;
	padding: 1rem;

	p {
		margin: 0;
	}
`;

const Table = styled.table`
	border-collapse: collapse;
	font-size: 0.8rem !important;
	margin: 0;
	width: 100%;

	td {
		background: #fff;
		border: none;
		border-top: 1px solid #ccc;
		font-family: 'Lato', sans-serif !important;
		font-size: 12px;
		padding: 0.5rem 0;

		&:last-child {
			padding: 0.5rem 0 0.5rem 1.5rem;
		}
	}

	tr {
		margin: 0;
	}

	tr:first-child > td {
		border-top: none;
		border-bottom: 2px solid #aaa;
		font-size: 16px;
		font-weight: 700;
	}
`;

const BackToMap = styled.p`
	color: hsl(210, 58%, 33%);
	cursor: pointer;
	font-size: 0.8rem;
	margin: 0 0 0.5rem;
	text-align: center;
`;

const ValueCell = styled.td`
	color: ${props => (props.color ? props.color : '#333')};
	font-family: 'Roboto Mono', monospace !important;
	min-width: 100px;
	text-align: right;
`;

interface Props {
	data: State['data'];
	year: number;
	counties: { id: number; name: string }[];
	activeCounty: number;
	updateActiveCounty: (d: number) => void;
	activeState: StateId;
	updateActiveState: (s: ActiveStateOrUSId) => void;
}

const CountyTable = ({
	data,
	year,
	counties,
	activeCounty,
	updateActiveCounty,
	activeState,
	updateActiveState,
}: Props) => {
	const [togglePercentages, setTogglePercentages] = useState(false);

	const { context } = useContext(MapContext);

	if (!data[year][activeCounty]) {
		console.log(year, activeCounty);
	}
	const countyTotals = showSumOfPolicies(data[year][activeCounty], context);

	return (
		<StyledCountyTable>
			<div>
				<BackToMap onClick={() => updateActiveState(0)}>
					← Back to US Map
				</BackToMap>
				{counties.length > 1 ? (
					<Select
						name="county"
						id="county"
						value={activeCounty}
						onChange={e => updateActiveCounty(+e.target.value)}
					>
						{counties.map(county => (
							<option key={`county-opt-${county.id}`} value={county.id}>
								{county.name}
							</option>
						))}
					</Select>
				) : activeState === 11 ? (
					<h3 style={{ textAlign: 'center' }}>District of Columbia</h3>
				) : (
					<h3 style={{ textAlign: 'center' }}>At-Large District</h3>
				)}
				<Button
					style={{ display: 'block' }}
					onClick={() => setTogglePercentages(!togglePercentages)}
				>
					{togglePercentages
						? 'Show in Dollars'
						: `Show as Percent of ${countyTotals >= 0 ? 'Increase' : 'Cut'}`}
				</Button>
				<Table>
					<tbody>
						<tr>
							<td>
								Average Tax {countyTotals >= 0 ? 'Increase' : 'Cut'} in {year}
							</td>
							<ValueCell color={countyTotals >= 0 ? '#ef4438' : '#00aa22'}>
								{`${countyTotals >= 0 ? '+' : ''}${formatter(countyTotals, '$')}`}
							</ValueCell>
						</tr>
						{POLICIES.map(policy => {
							const value = togglePercentages
								? data[year][activeCounty][policy.shorthand] /
									Math.abs(showSumOfPolicies(data[year][activeCounty], context))
								: data[year][activeCounty][policy.shorthand];
							return (
								<tr key={`policy-${policy.shorthand}`}>
									<td>{policy.abbr}</td>
									<ValueCell color={value >= 0 ? '#ef4438' : '#00aa22'}>
										{`${value >= 0 ? '+' : ''}${formatter(
											context[policy.id] ? value : 0,
											togglePercentages ? '%' : '$',
										)}`}
									</ValueCell>
								</tr>
							);
						})}
					</tbody>
				</Table>
			</div>
		</StyledCountyTable>
	);
};

export default CountyTable;
